import { Grid, Typography } from '@material-ui/core';
import Layout from '../../components/Layout/Layout.component';
import Breadcrumbs from '../../components/Breadcrumbs.component';
import { Paths } from '../../utils/routerUrls';
import { useStyles } from '../styles';
import useSessionUser from '../../hooks/useSessionUser';
import { useTranslation } from 'react-i18next';
import { StatisticsChart } from './StatisticsChart.component';
import { StatisticsChip } from './StatisticsChip.component';
import { useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { getAssessmentSessionsForStats } from '../../services/exams/assessmentSession/assessments.service';
import { AssessmentSessionForStatsDateResponse, ExamSheetForStats } from '../../models/session.types';
import { useLayoutContext } from '../../Context/Layout.context';
import { AxiosResponse } from 'axios';
import { SessionsList } from './SessionsList.component';
import { ExamSheetsList } from './ExamSheetsList.component';
import { getSmsStats } from '../../services/users/users.service';
import { ExamSheetStatName, SmsStatName, StatisticsChartData, StatisticsChipData } from './Statistics.types';
import { getSesionStats } from '../../services/scoring/scoring.service';

export const Statistics: React.FC = () => {
  const classes = useStyles();

  const { user } = useSessionUser();
  const { t } = useTranslation();
  const { showLoader } = useLayoutContext();

  const [assementSessions, setAssementSessions] = useState<AssessmentSessionForStatsDateResponse[]>([]);
  const [selectedAssementSession, setSelectedAssementSession] = useState<AssessmentSessionForStatsDateResponse>();
  const [selectedExamSheetId, setSelectedExamSheetId] = useState<string>('');
  const [smsStats, setSmsStats] = useState<StatisticsChipData[]>();
  const [examSheetStats, setExamSheetStats] = useState<StatisticsChipData[]>();
  const [examSheetCharts, setExamSheetCharts] = useState<StatisticsChartData[]>();

  const isAdmin = user.profile.account_type === 'administrator';

  const handleGetAssessmentSessions = () => {
    showLoader(true);
    getAssessmentSessionsForStats()
      .then(
        (result: AxiosResponse<AssessmentSessionForStatsDateResponse[]>) => {
          if (result.data.length) {
            setAssementSessions(result.data);
            if (!sessionStorage.getItem('selectedAssementSessionId')) {
              setSelectedAssementSession(result.data[0]);
              sessionStorage.setItem('selectedAssementSessionId', result.data[0].examSessionId);
            }
          }
        },
        () => {
          console.log('get_error');
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  const handleGetSmsStats = () => {
    showLoader(true);
    getSmsStats()
      .then(
        (result) => {
          if (result.data) {
            const smsStatsData: StatisticsChipData[] = [
              {
                id: '1',
                name: SmsStatName.allSmsCount,
                value: result.data.allSmsCount,
              },
              {
                id: '2',
                name: SmsStatName.smsNotificationCount,
                value: result.data.smsNotificationCount,
              },
              {
                id: '3',
                name: SmsStatName.twoFactorAuthSmsCount,
                value: result.data.twoFactorAuthSmsCount,
              },
            ];
            setSmsStats(smsStatsData);
          }
        },
        () => {
          console.log('get_error');
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  function sumObjectValues(object: { [key: string]: number }): number {
    return Object.values(object).reduce((accumulator: number, value: number) => {
      return accumulator + value;
    }, 0);
  }

  const handleGetSessionStats = () => {
    showLoader(true);
    if (selectedExamSheetId) {
      getSesionStats(selectedExamSheetId)
        .then(
          (result) => {
            if (result.data) {
              const examSheetStatsData: StatisticsChipData[] = [
                {
                  id: '1',
                  name: ExamSheetStatName.specialCaseStats,
                  value: sumObjectValues(result.data.specialCaseStats),
                },
                {
                  id: '2',
                  name: ExamSheetStatName.examineeSheetStatusesCount,
                  value: sumObjectValues(result.data.examineeSheetStatusesCount),
                },
                {
                  id: '3',
                  name: ExamSheetStatName.taskSolutionSheetsStatusesCount,
                  value: sumObjectValues(result.data.taskSolutionSheetsStatusesCount),
                },
              ];
              setExamSheetStats(examSheetStatsData);

              const examSheetChartsData: StatisticsChartData[] = [];

              examSheetChartsData.push(
                {
                  id: '1',
                  name: 'Szczególne przypadki',
                  items: [],
                },
                {
                  id: '2',
                  name: 'Zdający',
                  items: [],
                },
                {
                  id: '3',
                  name: 'Karty rozwiązań zadań',
                  items: [],
                },
                {
                  id: '4',
                  name: 'Liczba RGZ z każdym statusem',
                  items: [],
                },
                {
                  id: '5',
                  name: 'Liczba RGZ przydzielonych i nieprzydzielonych',
                  items: [],
                },
              );

              if (result.data.specialCaseStats.resolved) {
                examSheetChartsData[0].items.push({
                  id: '1',
                  value: result.data.specialCaseStats.resolved,
                  label: 'Rozwiązane',
                  color: '#4E8F5A',
                });
              }
              if (result.data.specialCaseStats.inProgress) {
                examSheetChartsData[0].items.push({
                  id: '2',
                  value: result.data.specialCaseStats.inProgress,
                  label: 'Nierozwiązane',
                  color: '#BC0000',
                });
              }

              if (result.data.examineeSheetStatusesCount.complete) {
                examSheetChartsData[1].items.push({
                  id: '1',
                  value: result.data.examineeSheetStatusesCount.complete,
                  label: 'Karta kompletna',
                  color: '#4E8F5A',
                });
              }
              if (result.data.examineeSheetStatusesCount.incomplete) {
                examSheetChartsData[1].items.push({
                  id: '2',
                  value: result.data.examineeSheetStatusesCount.incomplete,
                  label: 'Karta niekompletna',
                  color: '#FF9A02',
                });
              }
              if (result.data.examineeSheetStatusesCount.empty) {
                examSheetChartsData[1].items.push({
                  id: '3',
                  value: result.data.examineeSheetStatusesCount.empty,
                  label: 'Brak karty zdającego',
                  color: '#BC0000',
                });
              }

              if (result.data.taskSolutionSheetsStatusesCount.downloadError) {
                examSheetChartsData[2].items.push({
                  id: '1',
                  value: result.data.taskSolutionSheetsStatusesCount.downloadError,
                  label: 'Bład pobierania',
                  color: '#FF5C45',
                });
              }
              if (result.data.taskSolutionSheetsStatusesCount.scanError) {
                examSheetChartsData[2].items.push({
                  id: '2',
                  value: result.data.taskSolutionSheetsStatusesCount.scanError,
                  label: 'Błąd skanowania',
                  color: '#3560FF',
                });
              }
              if (result.data.taskSolutionSheetsStatusesCount.examSheetLack) {
                examSheetChartsData[2].items.push({
                  id: '3',
                  value: result.data.taskSolutionSheetsStatusesCount.examSheetLack,
                  label: 'Błąd akrusza',
                  color: '#EA2075',
                });
              }
              if (result.data.taskSolutionSheetsStatusesCount.examineeLack) {
                examSheetChartsData[2].items.push({
                  id: '4',
                  value: result.data.taskSolutionSheetsStatusesCount.examineeLack,
                  label: 'Brak zdającego',
                  color: '#60009B',
                });
              }
              if (result.data.taskSolutionSheetsStatusesCount.duplicatedSheet) {
                examSheetChartsData[2].items.push({
                  id: '5',
                  value: result.data.taskSolutionSheetsStatusesCount.duplicatedSheet,
                  label: 'Duplikat',
                  color: '#3560FF',
                });
              }
              if (result.data.taskSolutionSheetsStatusesCount.incompleteSheet) {
                examSheetChartsData[2].items.push({
                  id: '6',
                  value: result.data.taskSolutionSheetsStatusesCount.incompleteSheet,
                  label: 'Karta niekompletna',
                  color: '#EA2075',
                });
              }
              if (result.data.taskSolutionSheetsStatusesCount.completeSheet) {
                examSheetChartsData[2].items.push({
                  id: '7',
                  value: result.data.taskSolutionSheetsStatusesCount.completeSheet,
                  label: 'Kompletna',
                  color: '#347F42',
                });
              }
              if (result.data.taskSolutionSheetsStatusesCount.new) {
                examSheetChartsData[2].items.push({
                  id: '8',
                  value: result.data.taskSolutionSheetsStatusesCount.new,
                  label: 'Nowa',
                  color: '#FF9A02',
                });
              }

              if (result.data.examTaskGroupSolutionScoreStatesCount.new) {
                examSheetChartsData[3].items.push({
                  id: '1',
                  value: result.data.examTaskGroupSolutionScoreStatesCount.new,
                  label: 'Nowe',
                  color: '#9747FF',
                });
              }
              if (result.data.examTaskGroupSolutionScoreStatesCount.open) {
                examSheetChartsData[3].items.push({
                  id: '2',
                  value: result.data.examTaskGroupSolutionScoreStatesCount.open,
                  label: 'Otwarte',
                  color: '#3560FF',
                });
              }
              if (result.data.examTaskGroupSolutionScoreStatesCount.assessed) {
                examSheetChartsData[3].items.push({
                  id: '3',
                  value: result.data.examTaskGroupSolutionScoreStatesCount.assessed,
                  label: 'Ocenione',
                  color: '#347F42',
                });
              }
              if (result.data.examTaskGroupSolutionScoreStatesCount.closed) {
                examSheetChartsData[3].items.push({
                  id: '4',
                  value: result.data.examTaskGroupSolutionScoreStatesCount.closed,
                  label: 'Zamknięte',
                  color: '#EA2075',
                });
              }

              if (result.data.examTaskGroupSolutionDownloadedCount.downloaded) {
                examSheetChartsData[4].items.push({
                  id: '1',
                  value: result.data.examTaskGroupSolutionDownloadedCount.downloaded,
                  label: 'Przydzielone',
                  color: '#4E8F5A',
                });
              }
              if (result.data.examTaskGroupSolutionDownloadedCount.notDownloaded) {
                examSheetChartsData[4].items.push({
                  id: '2',
                  value: result.data.examTaskGroupSolutionDownloadedCount.notDownloaded,
                  label: 'Nieprzydzielone',
                  color: '#BC0000',
                });
              }

              setExamSheetCharts(examSheetChartsData);
            }
          },
          () => {
            console.log('get_error');
          },
        )
        .finally(() => {
          showLoader(false);
        });
    }
  };

  useEffect(() => {
    handleGetAssessmentSessions();
    handleGetSmsStats();
  }, []);

  useEffect(() => {
    if (selectedExamSheetId !== '') {
      handleGetSessionStats();
    }
  }, [selectedExamSheetId]);

  const handleChangeSession = (event: SelectChangeEvent<string>) => {
    const session = assementSessions.find((e) => e.examSessionId === event.target.value);
    if (session) {
      setSelectedAssementSession(session);
      sessionStorage.setItem('selectedAssementSessionId', session.examSessionId);
    }
  };

  const handleChangeExamSheet = (event: SelectChangeEvent<string>) => {
    if (selectedAssementSession) {
      const examSheet = selectedAssementSession.examSheets.find((e) => e.examSheetId === event.target.value);
      if (examSheet) {
        setSelectedExamSheetId(examSheet.examSheetId);
        sessionStorage.setItem('selectedExamSheetId', examSheet.examSheetId);
      }
    }
  };

  useEffect(() => {
    if (!assementSessions.length) return;
    const selectedAssementSessionId = sessionStorage.getItem('selectedAssementSessionId');
    const selectedExamSheetId = sessionStorage.getItem('selectedExamSheetId');
    if (selectedAssementSessionId) {
      const session = assementSessions.find((e) => e.examSessionId === selectedAssementSessionId);
      setSelectedAssementSession(session);
      if (session && selectedExamSheetId) {
        const examSheet = session.examSheets.find((e) => e.examSheetId === selectedExamSheetId);
        if (examSheet) {
          setSelectedExamSheetId(examSheet.examSheetId);
        }
      }
    }
  }, [assementSessions]);

  return (
    <>
      <Layout viewType={isAdmin ? 'admin' : 'fullwidthForExaminer'}>
        <div className={classes.tableAndBreadcrumContainer} style={{ overflow: 'auto' }}>
          <div className={classes.adminBreadcrumbHeader}>
            <Breadcrumbs listRoute={Paths.calendar} currentList={'Statystyki'} />
          </div>
          <div className={classes.tableWhiteCardFlex}>
            <div className={classes.filtersContainerFlex} style={{ flexDirection: 'column' }}>
              <Typography variant='h1' className={classes.gridHeader}>Statystyki</Typography>
              <Typography style={{ textAlign: 'left', margin: '15px 30px 0px 45px' }}>
                Wybierz poniżej arkusz, aby zaprezentować szczegółowe statystyki. Dane dotyczące wysyłanych wiadomości
                SMS prezentowane są dla wszystkich sesji, niezależnie od wybranych parametrów
              </Typography>
            </div>
            <Grid container style={{ margin: '15px' }}>
              {smsStats &&
                smsStats.map((data) => (
                  <div key={data.id}>
                    <StatisticsChip data={data} />
                  </div>
                ))}
            </Grid>

            <Grid
              container
              style={{ padding: '25px', position: 'relative', top: '-30px', left: '65px', textAlign: 'left' }}
            >
              {assementSessions.length && (
                <>
                  <div style={{ marginRight: '40px' }}>
                    <SessionsList
                      sessions={assementSessions}
                      selectedSession={selectedAssementSession ? selectedAssementSession : assementSessions[0]}
                      onChange={handleChangeSession}
                    />
                  </div>
                  <ExamSheetsList
                    session={selectedAssementSession}
                    selectedExamSheetId={selectedExamSheetId}
                    onChange={handleChangeExamSheet}
                  />
                </>
              )}
            </Grid>

            <Grid container style={{ margin: '15px' }}>
              {examSheetStats &&
                examSheetStats.map((data) => (
                  <div key={data.id}>
                    <StatisticsChip data={data} />
                  </div>
                ))}
            </Grid>

            <Grid container style={{ margin: '15px 15px 0px 15px' }}>
              {examSheetCharts &&
                examSheetCharts.map((data) => (
                  <div key={data.id}>
                    <StatisticsChart data={data} />
                  </div>
                ))}
            </Grid>
          </div>
        </div>
      </Layout>
    </>
  );
};
